import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, FileQuestion, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { isNotFoundError } from "@/lib/query-errors";

interface DetailNotFoundStateProps {
  breadcrumbs: { label: string; to?: string }[];
  entityLabel?: string;
  backTo?: string;
  codeOrId?: string;
  /** Lỗi của query: lỗi khác 404 (mất mạng, lỗi máy chủ) hiển thị "Không tải được" + nút Thử lại */
  error?: unknown;
  onRetry?: () => void;
}

/** Chỉ hạ chữ cái đầu ("Phiếu NCR" → "phiếu NCR"), giữ nguyên các từ viết tắt */
const lowerFirst = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/**
 * Trang chi tiết khi không có dữ liệu: người dùng gõ/dán URL có id sai (404),
 * hoặc không tải được do lỗi kết nối/máy chủ (cho phép thử lại).
 */
export default function DetailNotFoundState({
  breadcrumbs,
  entityLabel = "Dữ liệu",
  backTo,
  codeOrId,
  error,
  onRetry,
}: DetailNotFoundStateProps) {
  const navigate = useNavigate();
  const targetUrl = backTo || breadcrumbs[0]?.to || "/";
  const label = lowerFirst(entityLabel);
  const isLoadError = Boolean(error) && !isNotFoundError(error);

  return (
    <StandardDetailLayout
      breadcrumbs={breadcrumbs}
      title={isLoadError ? `Không tải được ${label}` : `404 - Không tìm thấy ${label}`}
      showContextPanel={false}
    >
      <div
        data-testid={isLoadError ? "detail-load-error" : "detail-not-found-404"}
        className="bg-white border border-slate-200 rounded-xl p-10 my-4 text-center max-w-xl mx-auto shadow-xs"
      >
        <div
          className={
            isLoadError
              ? "flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 mb-4 mx-auto"
              : "flex items-center justify-center w-16 h-16 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 mb-4 mx-auto"
          }
        >
          {isLoadError ? <WifiOff className="h-8 w-8" /> : <FileQuestion className="h-8 w-8" />}
        </div>
        {!isLoadError && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-extrabold tracking-wider uppercase mb-3 mx-auto">
            <AlertCircle className="h-3.5 w-3.5" />
            Lỗi 404 — Not Found
          </div>
        )}
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          {isLoadError ? `Không tải được ${label}` : `Không tìm thấy ${label}`}
        </h2>
        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
          {isLoadError ? (
            "Kết nối tới máy chủ bị gián đoạn hoặc máy chủ đang bận. Vui lòng thử lại sau ít phút."
          ) : (
            <>
              {entityLabel}{" "}
              {codeOrId ? (
                <span className="font-mono font-semibold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded break-all">
                  {codeOrId}
                </span>
              ) : null}{" "}
              không tồn tại hoặc đã bị xóa khỏi hệ thống. Vui lòng kiểm tra lại đường dẫn hoặc quay về trang danh sách.
            </>
          )}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {isLoadError && onRetry && (
            <Button type="button" onClick={onRetry} className="gap-2 font-semibold">
              <RefreshCw className="h-4 w-4" />
              Thử lại
            </Button>
          )}
          <Button
            type="button"
            variant={isLoadError ? "outline" : "default"}
            onClick={() => navigate({ to: targetUrl as any })}
            className="gap-2 font-semibold"
          >
            <ArrowLeft className="h-4 w-4" />
            Quay lại danh sách {label}
          </Button>
        </div>
      </div>
    </StandardDetailLayout>
  );
}
